import { type RoutineLibraryItem } from '@littlebird/sdk';
import { useEffect } from 'react';

import { trackEvent } from '@/utils/analytics';

export type TemplateSource = 'system' | 'team' | 'generated' | 'custom';
export type TemplateSurface = 'suggestions' | 'team';

const CUSTOM_TEMPLATE_ID = 'custom';
const GENERATED_TEMPLATE_ID_PREFIX = 'generated-';

export const templateSourceOf = (item: RoutineLibraryItem): TemplateSource =>
  item.source ??
  (item.id.startsWith(GENERATED_TEMPLATE_ID_PREFIX) ? 'generated' : 'system');

export const templateSourceFor = (
  templateId: string,
  libraryItems: RoutineLibraryItem[]
): TemplateSource => {
  if (templateId === CUSTOM_TEMPLATE_ID) return 'custom';
  const item = libraryItems.find(
    (libraryItem) => libraryItem.id === templateId
  );
  if (item) return templateSourceOf(item);
  return templateId.startsWith(GENERATED_TEMPLATE_ID_PREFIX)
    ? 'generated'
    : 'system';
};

const reportedImpressions = new Set<string>();

export const useTemplateImpressions = (
  surface: TemplateSurface,
  items: RoutineLibraryItem[]
) => {
  const shownIds = items.map((item) => item.id).join(',');

  useEffect(() => {
    items.forEach((item, index) => {
      const impressionKey = `${surface}:${item.id}`;
      if (reportedImpressions.has(impressionKey)) return;
      reportedImpressions.add(impressionKey);

      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'library_template_shown',
        templateId: item.id,
        templateTitle: item.title,
        templateSource: templateSourceOf(item),
        surface,
        position: index + 1,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface, shownIds]);
};
